import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import pluralize from 'pluralize';

import { Button } from '@components/base';
import FilterDefinition, { LogicalOperator } from '@models/FilterDefinition';
import { FilterField, FilterGroup as FilterGroupType } from '@models/SearchFilter';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';
import { getNewFilterField } from '@utils/filterUtils';

import FilterGroup from './FilterGroup';
import FilterRow from './FilterRow';
import WithCombinatorMenu from './WithCombinatorMenu';

type FilterListProps = {
  filterDefinitionPath?: FilterDefinition[];
  filterDefs: FilterDefinition[];
  filterGroup: FilterGroupType;
  nestingLevel?: number;
  onChange: (filterGroup: FilterGroupType) => void;
};

const FilterList: FC<FilterListProps> = ({
  filterDefinitionPath = [],
  filterDefs,
  filterGroup,
  nestingLevel = 0,
  onChange,
}) => {
  const { t } = useTranslation();

  const {
    filterDefLoad: { data: filterDefinitions },
    isReadonly,
  } = useSearchAndFilter();

  const mounted = useRef(false);

  const [autoFocusId, setAutoFocusId] = useState('');

  const onFilterFieldsChange = useCallback(
    (fields: FilterField[]) => onChange({ ...filterGroup, fields }),
    [filterGroup, onChange]
  );

  const handleAddField = useCallback(
    (isRoot?: boolean) => {
      const newFilter = getNewFilterField(isRoot);
      if (mounted.current) setAutoFocusId(newFilter.id);
      onFilterFieldsChange([...(filterGroup.fields ?? []), newFilter]);
    },
    [filterGroup.fields, onFilterFieldsChange]
  );

  useEffect(() => {
    if (!filterGroup.fields?.length) handleAddField();
    else mounted.current = true;
  }, [filterGroup.fields, filterGroup.fields?.length, handleAddField]);

  const handleFilterChange = useCallback(
    (id: string) => (filter: FilterField) =>
      onFilterFieldsChange(filterGroup.fields?.map(f => (f.id === id ? filter : f))),
    [filterGroup.fields, onFilterFieldsChange]
  );

  const handleRemoveFilter = (id: string) => () =>
    onFilterFieldsChange(filterGroup.fields?.filter(f => f.id !== id));

  const handleCombinatorChange = (logicalOperator: LogicalOperator) =>
    onChange({ ...filterGroup, logicalOperator });

  const nestedFilterListChangeHandler = useCallback(
    (filterFieldId: string) => (nestedFilterGroup: FilterGroupType) =>
      onFilterFieldsChange(
        filterGroup.fields.map(filterField =>
          filterField.id === filterFieldId
            ? { ...filterField, filter: nestedFilterGroup }
            : filterField
        )
      ),
    [filterGroup.fields, onFilterFieldsChange]
  );

  let groupCount = 0;

  return (
    <Stack spacing={1}>
      <WithCombinatorMenu
        disabled={isReadonly}
        hideMenu={!filterGroup.fields || (filterGroup.fields && filterGroup.fields.length < 2)}
        onChange={handleCombinatorChange}
        value={filterGroup.logicalOperator}
      >
        <Stack flex={1} spacing={0.5}>
          {filterGroup.fields?.map(filterField =>
            filterField.filter?.isRootLevelFilter && filterDefinitions ? (
              <FilterGroup
                filterGroup={filterField.filter}
                index={groupCount++}
                key={filterField.id}
                nestingLevel={nestingLevel}
                removeFilterGroup={handleRemoveFilter(filterField.id)}
                updateFilterGroup={nestedFilterListChangeHandler(filterField.id)}
              />
            ) : (
              <FilterRow
                autoFocus={autoFocusId === filterField.id}
                filterDefinitionPath={filterDefinitionPath}
                filterDefinitions={filterDefs}
                filterField={filterField}
                key={filterField.id}
                nestingLevel={nestingLevel}
                onChange={handleFilterChange(filterField.id)}
                onRemove={
                  !filterField.propertyName && filterGroup.fields?.length === 1
                    ? undefined
                    : handleRemoveFilter(filterField.id)
                }
              />
            )
          )}
        </Stack>
      </WithCombinatorMenu>

      {!isReadonly && (
        <Stack direction='row' spacing={1}>
          <Button leftIcon='add' onClick={() => handleAddField()}>
            {filterDefinitionPath.length > 0
              ? t('filter.addItem', {
                  item: pluralize
                    .singular(filterDefinitionPath[filterDefinitionPath.length - 1].displayName)
                    .toLowerCase(),
                })
              : t('filter.add')}
          </Button>

          {nestingLevel < 2 && (
            <Button leftIcon='add' onClick={() => handleAddField(true)}>
              {t('filter.addSubGroup')}
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default FilterList;
